import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface WODocumentSettings {
  id: string;
  company_id: string;
  show_project_name: boolean;
  show_site_address: boolean;
  show_site_contact: boolean;
  show_mobilization_date: boolean;
  show_completion_date: boolean;
  show_other_subs: boolean;
  show_line_items: boolean;
  show_product_specs: boolean;
  show_special_instructions: boolean;
  show_sov_reference: boolean;
  show_internal_codes: boolean;
  show_wo_total: boolean;
  show_payment_terms: boolean;
  show_retainage: boolean;
  show_lien_waiver_req: boolean;
  show_billing_instructions: boolean;
  show_work_hours: boolean;
  show_parking: boolean;
  show_badge_requirements: boolean;
  show_safety_requirements: boolean;
  show_signature_block: boolean;
  show_sub_signature: boolean;
  show_company_signature: boolean;
  show_contractor_scope: boolean;
  custom_footer_text: string | null;
  contractor_scope_text: string | null;
  section_order: string[] | null;
}

export const TOGGLE_KEYS = [
  "show_project_name", "show_site_address", "show_site_contact",
  "show_mobilization_date", "show_completion_date", "show_other_subs",
  "show_line_items", "show_product_specs", "show_special_instructions",
  "show_sov_reference", "show_internal_codes",
  "show_wo_total", "show_payment_terms", "show_retainage",
  "show_lien_waiver_req", "show_billing_instructions",
  "show_work_hours", "show_parking", "show_badge_requirements",
  "show_safety_requirements",
  "show_signature_block", "show_sub_signature", "show_company_signature",
  "show_contractor_scope",
] as const;

export const DEFAULT_TOGGLES: Record<string, boolean> = {
  show_project_name: true, show_site_address: true, show_site_contact: true,
  show_mobilization_date: true, show_completion_date: true, show_other_subs: false,
  show_line_items: true, show_product_specs: true, show_special_instructions: true,
  show_sov_reference: false, show_internal_codes: false,
  show_wo_total: true, show_payment_terms: true, show_retainage: true,
  show_lien_waiver_req: true, show_billing_instructions: true,
  show_work_hours: true, show_parking: true, show_badge_requirements: true,
  show_safety_requirements: true,
  show_signature_block: true, show_sub_signature: true, show_company_signature: true,
  show_contractor_scope: true,
};

export const DEFAULT_SECTION_ORDER = ["scope", "site_info", "terms", "contractor_scope", "signatures"];

export const useWODocumentSettings = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["wo_document_settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wo_document_settings" as any)
        .select("*")
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as WODocumentSettings | null;
    },
    enabled: !!user,
  });
};

export const useUpsertWODocumentSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<WODocumentSettings>) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const { error } = await supabase
        .from("wo_document_settings" as any)
        .upsert({ ...settings, company_id: companyId } as any, { onConflict: "company_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wo_document_settings"] }),
  });
};
